import moment from 'moment';
import Promise from 'bluebird';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import actions from '../../actions';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import { FEATURE_ENABLEMENT_STATUS, REPORT_TYPE_OPTIONS } from '../../constants/ReportOptions';
import {
	Button,
	Checkbox,
	Dropdown,
	Form,
	FormControl,
	Loader,
	Spinner,
} from '../../components';
import graphqlLib from '../../lib/graphql-lib';

import '../../../scss/containers/reports/_get.scss';

const userShape = PropTypes.shape({
	id: PropTypes.number.required,
	email: PropTypes.string.required,
	firstName: PropTypes.string.required,
	lastName: PropTypes.string.required,
});

export class GetReport extends Component {

	static propTypes = {
		user: userShape,
		users: PropTypes.arrayOf(userShape),
	};

	state = {
		type: null,
		customers: [],
		selectedCustomers: [],
		searchManagersResults: [],
		searchManagersPromise: null,
	};

	componentDidMount () {

		if (!this.props.user.can('view-report')) {
			browserHistory.push('/');
		}

	}

	componentWillUnmount () {

		// Cancel outstanding promises
		if (this.state.searchManagersPromise) {
			this.state.searchManagersPromise.cancel();
		}

	}

	componentWillReceiveProps (nextProps) {

		const newState = {};

		if (nextProps.customers.customers !== this.props.customers.customers) {

			// Select all customers by default
			newState.selectedCustomers = nextProps.customers.customers ? nextProps.customers.customers.results.map(c => c.rawId) : [];

		}

		// Only trigger csv download when a report request has finished and there is data
		if (!nextProps.isFetchingReport && this.props.isFetchingReport && nextProps.data !== null) {

			const csvObj = window.URL.createObjectURL(new Blob([nextProps.data], { type: 'text/csv' }));
			const timestamp = moment().format('MM-DD-YYYY_HH-mm-ss')
			const fileName = `${this.state.type}_${timestamp}.csv`;
			const link = document.createElement('a');

			link.setAttribute('href', csvObj);
			link.setAttribute('download', fileName);

			// Temporarily append element to DOM to ensure download
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			setTimeout(() => window.URL.revokeObjectURL(csvObj), 500);

		}

		// Set the new state if any keys were changed
		if (Object.keys(newState).length) {
			this.setState(newState);
		}

	}

	searchManagers = async (query) => {

		// Skip if there's already an outstanding request
		if (this.state.searchManagersPromise) {
			this.state.searchManagersPromise.cancel();
		}

		// If there is no query value then clear the results
		if (!query) {

			this.setState({
				searchManagersResults: [],
				searchManagersPromise: null,
			});

			return;
		}

		const searchManagersPromise = Promise.resolve(this.props.client.query({
			query: searchManagersQuery,
			variables: { query: `${query}%` },
		}));

		this.setState({ searchManagersPromise });

		try {

			const { data: { users: { results } } } = await searchManagersPromise;

			this.setState({
				searchManagersResults: results,
				searchManagersPromise: null,
			});

		} catch (err) {

			this.props.actions.setGlobalMsg('error', 'Could not search users.');

			this.setState({
				searchManagersResults: [],
				searchManagersPromise: null,
			});

		}

	}

	handleSubmitForm = e => {
		e.preventDefault();

		this.props.actions.fetchReport(this.state.type, this.state.selectedCustomers);

	}

	handleCustomersToggle = e => {

		if (e.target.checked) {
			this.setState({ selectedCustomers: this.props.customers.customers.results.map(c => c.rawId) });
		} else {
			this.setState({ selectedCustomers: [] });
		}

	}

	handleCustomerSelected = e => {

		const rawId = Number(e.target.value);

		let selectedCustomers = [...this.state.selectedCustomers];

		if (selectedCustomers.includes(rawId)) {
			selectedCustomers = selectedCustomers.filter(d => d !== rawId);
		} else {
			selectedCustomers.push(rawId);
		}

		this.setState({ selectedCustomers });

	}

	handleManagerSelected = option => {

		browserHistory.push({
			...this.props.location,
			query: {
				...this.props.location.query,
				managerUserId: option.value,
			},
		});

	}

	formatManagerOption (manager) {
		return {
			label: (
				<span>
					{ manager.fullName }<br />
					<small>{ manager.email }</small>
				</span>
			),
			value: manager.id,
		};
	}

	render () {

		const selectedManagerOption = this.formatManagerOption(this.props.manager.user || this.props.user);
		const managerOptions = this.state.searchManagersResults.map(this.formatManagerOption);
		const customers = this.props.customers.loading || !this.props.customers.customers ? [] : this.props.customers.customers.results;
		const isSubmitDisabled = this.state.type === null || (this.state.type === FEATURE_ENABLEMENT_STATUS && (this.state.selectedCustomers.length === 0 || this.props.customers.loading)) || this.props.isFetchingReport;

		return (
			<div className="container-narrow reports">
				<h1>Reports</h1>
				<p>Generate your reports here and download as CSV file.</p>
				<Form onSubmit={ this.handleSubmitForm } className="loader-parent">
					<Loader overlay visible={ this.props.isFetchingReport } />
					<FormControl>
						<label>Report Type</label>
						<div className="form-input">
							<Dropdown
								name="type"
								placeholder="Select Report Type"
								options={ REPORT_TYPE_OPTIONS }
								selected={ this.state.type }
								disabled={ this.props.isFetchingReport }
								onChange={ type => this.setState({ type }) } />
						</div>
					</FormControl>
				
					<If condition={ this.state.type === FEATURE_ENABLEMENT_STATUS }>
						<FormControl>
							<label>Account Manager</label>
							<div className="form-input">
								<Dropdown
									autoComplete={ this.searchManagers }
									isFetchingOptions={ this.state.searchManagersPromise !== null }
									name="managerUserId"
									placeholder="Search account managers..."
									options={ managerOptions }
									selected={ selectedManagerOption }
									disabled={ this.props.isFetchingReport }
									onChange={ this.handleManagerSelected } />
							</div>
						</FormControl>

						<FormControl>
							<label>Customer(s)</label>
							<Choose>
								<When condition={ this.props.customers.loading }>
									<div className="loading">
										<Spinner style="inline" />
										<span>Loading customers...</span>
									</div>
								</When>
								<When condition={ customers.length > 0 }>
									<div className="form-input check-group">
										<div className="check-all">
											<Checkbox
												name="all-customers"
												label="All"
												checked={ customers.length === this.state.selectedCustomers.length }
												disabled={ this.props.isFetchingReport }
												onChange={ this.handleCustomersToggle } />
										</div>
										<div className="check-items">
											{ customers.map(customer => {
												return (
													<Checkbox
														key={ customer.id }
														name={ `customer-${customer.id}` }
														value={ customer.rawId }
														label={ customer.name }
														checked={ this.state.selectedCustomers.includes(customer.rawId) }
														onChange={ this.handleCustomerSelected }
														disabled={ this.props.isFetchingReport } />
												);
											}) }
										</div>
									</div>
								</When>
								<Otherwise>
									<p>The selected user does not manage any customers</p>
								</Otherwise>
							</Choose>
						</FormControl>
					</If>
					<Button type="submit" disabled={ isSubmitDisabled }>Generate Report</Button>

				</Form>
			</div>
		);

	}

}

const managerQuery = gql`
	query getCustomerManager($userId: EncodedID!) {
		user(id: $userId) {
			id
			fullName
			email
		}
	}
`;

const searchManagersQuery = gql`
	query searchCustomerManagers($query: String!) {
		users(filter: { query: $query, roleId: { eq: ACCOUNT_MANAGEMENT }, isEnabled: { eq: true } }) {
			results {
				id
				fullName
				email
			}
		}
	}
`;

const customersQuery = gql`
	query getCustomers($managerUserId: EncodedID) {
		customers(filter: { managerUserId: { eq: $managerUserId } }, limit: 0) {
			results {
				id
				rawId: id(raw: true)
				name
			}
		}
	}
`;

export default compose(
	connect(
		state => ({
			user: state.auth.user,
			isFetchingReport: state.reports.isFetching,
			data: state.reports.data,
			error: state.reports.error,
		}),
		dispatch => ({
			actions: {
				...bindActionCreators(actions.reports, dispatch),
				setGlobalMsg: bindActionCreators(actions.globalMsg.setGlobalMsg, dispatch),
			},
		})
	),
	graphql(managerQuery, {
		name: 'manager',
		options: props => {

			const userId = props.location.query.managerUserId || props.user.id;

			return {
				variables: { userId },
			};
		},
	}),
	graphql(customersQuery, {
		name: 'customers',
		options: props => {

			const managerUserId = props.location.query.managerUserId || props.user.id;

			return {
				variables: { managerUserId },
			};
		},
	})
)(GetReport);




// WEBPACK FOOTER //
// ./src/assets/js/containers/reports/Get.js